import React, { useState, useEffect } from "react";
import { useMembers } from "../hooks/useMembers";
import { logSms } from "../lib/smsLog";

interface Props {
  preset?: string;
  onPresetUsed?: () => void;
}

const TR_CHARS = /[şğüöçıŞĞÜÖÇİ]/;
const GSM_LIMIT = 160;
const UNICODE_LIMIT = 70;

function getSmsInfo(text: string) {
  const hasUnicode = TR_CHARS.test(text);
  const limit = hasUnicode ? UNICODE_LIMIT : GSM_LIMIT;
  const parts = Math.ceil(text.length / limit) || 1;
  const remaining = parts * limit - text.length;
  return { hasUnicode, limit, parts, remaining };
}

const SmsPage: React.FC<Props> = ({ preset, onPresetUsed }) => {
  const { members, loading, error } = useMembers();
  const [mode, setMode] = useState<"all" | "selected" | "manual">("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [manualPhone, setManualPhone] = useState("");

  // Şablon sayfasından gelen mesajı yükle
  useEffect(() => {
    if (preset) {
      setMessage(preset);
      onPresetUsed?.();
    }
  }, [preset, onPresetUsed]);

  const filtered = members.filter(m =>
    !search || m.adSoyad.toLowerCase().includes(search.toLowerCase()) || m.telefon.includes(search)
  );

  const toggleSelect = (id: string) =>
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const getRecipients = (): { name: string; phone: string }[] => {
    if (mode === "manual") {
      return manualPhone.trim() ? [{ name: "Manuel", phone: manualPhone.trim() }] : [];
    }
    const src = mode === "all" ? members : members.filter(m => selected.has(m.id));
    return src.filter(m => m.telefon).map(m => ({ name: m.adSoyad, phone: m.telefon }));
  };

  const recipients = getRecipients();
  const smsInfo = getSmsInfo(message);

  const handleSend = async () => {
    if (!message.trim()) return;
    if (!recipients.length) { setResult({ ok: false, msg: "Alıcı seçilmedi veya telefon numarası girilmedi." }); return; }

    setSending(true);
    setResult(null);

    try {
      // SMS gönderim (Netgsm) — ŞU AN KULLANILMIYOR ama altyapı hazır, kod korundu.
      const res = await fetch("/.netlify/functions/send-sms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phones: recipients.map(r => r.phone), message: message.trim() }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setResult({ ok: true, msg: `${recipients.length} kişiye SMS başarıyla gönderildi.` });
        recipients.forEach(r => logSms({ alici: r.phone, aliciAd: r.name, mesaj: message.trim(), durum: "gönderildi" }));
        setMessage("");
        setSelected(new Set());
      } else {
        setResult({ ok: false, msg: data.error || "SMS gönderilemedi. Netgsm ayarlarını kontrol edin." });
        recipients.forEach(r => logSms({ alici: r.phone, aliciAd: r.name, mesaj: message.trim(), durum: "hata" }));
      }
    } catch {
      setResult({ ok: false, msg: "Sunucuya bağlanılamadı. Netlify Function çalışıyor mu?" });
    }

    setSending(false);
  };

  const noPhone = mode === "all" ? members.filter(m => !m.telefon).length : 0;

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800">SMS Gönder</h2>
        <p className="text-sm text-gray-500 mt-0.5">Netgsm altyapısıyla toplu veya tekil SMS gönderin</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-gray-500 py-20 justify-center">
          <i className="fa-solid fa-circle-notch fa-spin text-2xl text-[#8B1428]" /> Üyeler yükleniyor...
        </div>
      ) : error ? (
        <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">Üyeler yüklenemedi.</div>
      ) : (
      <>
      {/* Netgsm uyarısı */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex gap-3">
        <i className="fa-solid fa-circle-info text-blue-500 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-700">
          <strong>Netgsm API gerekli:</strong> Hesap onaylandıktan sonra Netlify Dashboard → Environment Variables bölümüne
          {" "}<code className="bg-blue-100 px-1 rounded">NETGSM_USER</code>,
          {" "}<code className="bg-blue-100 px-1 rounded">NETGSM_PASS</code>,
          {" "}<code className="bg-blue-100 px-1 rounded">NETGSM_HEADER</code> ekleyin.
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Sol: Alıcı seçimi */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="font-bold text-gray-700 mb-4">Alıcılar</h3>

          <div className="flex gap-2 mb-4 flex-wrap">
            {[
              { id: "all", label: `Tüm Üyeler (${members.length})`, icon: "fa-users" },
              { id: "selected", label: "Seçili Üyeler", icon: "fa-check-square" },
              { id: "manual", label: "Manuel Numara", icon: "fa-phone" },
            ].map(opt => (
              <button key={opt.id} onClick={() => setMode(opt.id as any)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-colors
                  ${mode === opt.id ? "bg-[#8B1428] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                <i className={`fa-solid ${opt.icon}`} /> {opt.label}
              </button>
            ))}
          </div>

          {mode === "manual" && (
            <input type="tel" placeholder="05XX XXX XX XX" value={manualPhone}
              onChange={e => setManualPhone(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30" />
          )}

          {mode === "all" && members.length === 0 && (
            <p className="text-sm text-gray-400 mt-2">Üye listesi henüz yüklenmedi.</p>
          )}

          {mode === "all" && noPhone > 0 && (
            <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <i className="fa-solid fa-triangle-exclamation" />
              {noPhone} üyenin telefon numarası yok — SMS gönderilemez.
            </div>
          )}

          {mode === "selected" && (
            <>
              <div className="relative mb-3">
                <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input type="text" placeholder="Üye ara..." value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30" />
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {filtered.map(m => (
                  <label key={m.id} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={selected.has(m.id)}
                      onChange={() => toggleSelect(m.id)} className="accent-[#8B1428]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-700 truncate">{m.adSoyad}</div>
                      <div className="text-xs text-gray-400 font-mono">{m.telefon || "Telefon yok"}</div>
                    </div>
                    {!m.telefon && <i className="fa-solid fa-triangle-exclamation text-amber-400 text-xs" />}
                  </label>
                ))}
              </div>
              {selected.size > 0 && (
                <div className="mt-2 text-xs text-[#8B1428] font-semibold">{selected.size} kişi seçildi</div>
              )}
            </>
          )}
        </div>

        {/* Sağ: Mesaj */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col">
          <h3 className="font-bold text-gray-700 mb-4">Mesaj</h3>

          <textarea
            rows={7}
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="SMS mesajınızı buraya yazın..."
            className="flex-1 w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30"
          />

          {/* Karakter sayacı */}
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">{message.length} karakter · {smsInfo.parts} SMS · {smsInfo.remaining} karakter kaldı</span>
              <span className={`font-semibold ${smsInfo.hasUnicode ? "text-amber-600" : "text-gray-400"}`}>
                {smsInfo.hasUnicode ? `Unicode (${UNICODE_LIMIT}/SMS)` : `GSM (${GSM_LIMIT}/SMS)`}
              </span>
            </div>
            {smsInfo.hasUnicode && (
              <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <i className="fa-solid fa-triangle-exclamation mt-0.5 flex-shrink-0" />
                <span>Mesajda Türkçe karakter var. Her SMS <strong>{UNICODE_LIMIT} karakter</strong> ile sınırlı.
                  Türkçe harf kullanmadan yazarsanız <strong>{GSM_LIMIT} karakter/SMS</strong> olur ve daha az SMS harcar.</span>
              </div>
            )}
          </div>

          {/* Özet */}
          <div className="mt-4 bg-gray-50 rounded-xl p-4 text-sm">
            <div className="flex justify-between mb-1">
              <span className="text-gray-500">Alıcı sayısı</span>
              <span className="font-bold text-gray-700">{recipients.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Toplam SMS adedi</span>
              <span className="font-bold text-gray-700">{recipients.length * smsInfo.parts}</span>
            </div>
          </div>

          {result && (
            <div className={`mt-3 flex items-start gap-2 text-sm px-4 py-3 rounded-lg
              ${result.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
              <i className={`fa-solid ${result.ok ? "fa-circle-check" : "fa-triangle-exclamation"} mt-0.5`} />
              {result.msg}
            </div>
          )}

          <button onClick={handleSend} disabled={sending || !message.trim() || !recipients.length}
            className="mt-4 flex items-center justify-center gap-2 text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(135deg, #8B1428, #0F4C81)" }}>
            {sending
              ? <><i className="fa-solid fa-circle-notch fa-spin" /> Gönderiliyor...</>
              : <><i className="fa-solid fa-paper-plane" /> {recipients.length} Kişiye SMS Gönder</>}
          </button>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default SmsPage;
